<template>
  <div>
    <label>{{ $t('background') }}</label>

    <div class="background-image-container">
      <div v-if="!background.originalUrl" class="background-box"></div>
      <img v-else class="background-box" :src="background.originalUrl" />

      <div v-if="!isUploading">
        <div class="buttons-container">
          <button
            id="crop"
            v-if="croppable && background.originalUrl"
            class="background-button crop-button"
            @click="setOverlay({ component: 'Background', data: {} })"
          >
            <i class="fas fa-crop-alt"></i>
            <Tooltip :target="'crop'" :text="$t('crop')"></Tooltip>
          </button>
          <button id="add-change-button" class="background-button change-button mx-1" @click="$refs.imageRef.click()">
            <i class="fas fa-camera"></i>
            <Tooltip
              :target="'add-change-button'"
              :text="background.originalUrl ? $t('change-image') : $t('insert-image')"
            >
            </Tooltip>
          </button>
          <button
            v-if="background.originalUrl"
            id="remove-button"
            class="background-button remove-button"
            @click="removeImage"
          >
            <i class="fas fa-trash-alt"></i>
            <Tooltip :target="'remove-button'" :text="$t('remove-image')"></Tooltip>
          </button>
        </div>
      </div>
      <div v-else id="div-spinner">
        <Spinner id="spinner"></Spinner>
      </div>
    </div>

    <input ref="imageRef" type="file" accept="image/*" @change="handleFile" hidden />

    <div class="d-flex flex-column mb-3">
      <div class="p-2">
        <Toggle :label="$t('image-priority')" :value="background.isImagePriority" @toggle="propsChanged($event)" />
      </div>
      <div class="p-2">
        <ColorPicker :label="$t('color')" :color="background.color" @submit="colorChanged($event)" />
      </div>
    </div>
  </div>
</template>

<script>
import ColorPicker from '@/shared/components/ColorPicker'
import Spinner from '@/shared/components/Spinner'
import Toggle from '@/shared/components/Toggle'
import UploadFileService from '@/services/UploadFile.js'
import { mapActions } from 'vuex'
import { infoAlert, successAlert, errorAlert } from '@/helpers/alertHelper.js'

export default {
  name: 'Background',
  i18nOptions: { keyPrefix: 'components.sidenav.shared.background' },
  components: { ColorPicker, Spinner, Toggle },
  props: {
    croppable: {
      type: Boolean,
      default: true,
    },
    uploading: {
      type: Boolean,
      default: false,
    },
    background: {
      type: Object,
      default() {
        return {
          originalUrl: '',
          url: '',
          points: [],
          zoom: 1,
          orientation: 1,
          color: 'transparent',
          isImagePriority: false,
        }
      },
    },
  },
  data() {
    return {
      isUploading: this.uploading,
    }
  },
  methods: {
    ...mapActions('ui', ['setOverlay']),
    handleFile(event) {
      this.isUploading = true
      infoAlert(this.$i18n.t('upload-file.upload-init'))
      UploadFileService.uploadCore(event.target.files[0])
        .then(response => {
          const url = response.data.data.uploadAsset.url
          this.$emit('change', {
            ...this.background,
            url,
            originalUrl: url,
          })
          this.isUploading = false
          successAlert(this.$i18n.t('upload-file.success'))
        })
        .catch(() => {
          this.isUploading = false
          errorAlert(this.$i18n.t('upload-file.error'))
        })
    },
    colorChanged(color) {
      this.$emit('change', {
        ...this.background,
        color,
      })
    },
    removeImage() {
      this.$emit('change', {
        ...this.background,
        originalUrl: '',
        url: '',
        points: [],
        zoom: 1,
        orientation: 1,
        isImagePriority: false,
      })
    },
    propsChanged(isImagePriority) {
      this.$emit('change', {
        ...this.background,
        isImagePriority,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
label {
  display: block;
  font-weight: bold;
  margin-bottom: 10px;
}

.background-image-container {
  position: relative;
  box-shadow: 1px 1px 15px -5px black;
  border-radius: 5px;
  margin-bottom: 10px;

  .background-box {
    width: 100%;
    height: 100px;
    object-fit: cover;
    border-radius: 5px;
    vertical-align: top;
    background-color: #c5c5c5;
  }

  #div-spinner {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;

    background: rgba(255, 255, 255, 0.5);
  }

  .buttons-container {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;

    .background-button {
      border: none;
      outline: none;
      cursor: pointer;
      background: #ececec;
      border: 1px solid #858585;
      &.crop-button {
        color: #000000;
      }
      &.remove-button {
        color: #ff4646;
      }
      &.change-button {
        color: #000000;
      }
      &:disabled {
        cursor: not-allowed;
      }
    }
  }
}
</style>
